import React, { useEffect, useRef, useState } from "react";
import { shareViaFlutter } from "../../../utils/shareLink";
import {
  leagueShareData,
} from "../../../utils/shareMessage";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CopyIcon,
  GlobeIcon,
  PrivateLeagueMarkIcon,
  ShareUpIcon,
} from "./icons";
import { VARIANTS } from "./wizard/leagueWizardConfig";
import WizardHeader, { HEADER_HEIGHT } from "./wizard/WizardHeader";
import { scrollToTop, useFocusedFlowChrome } from "./wizard/wizardScroll";
import useWindowWidth from "../../../hooks/useWindowWidth";

// The last screen of the create flow — one component for both leagues, because
// the hand-off is the same job either way: here's your code, here's your link,
// here's the league. Only the copy and the link prefix differ, and the prefix
// comes from the same variant config the wizard used to build the link, so the
// address shown here can never disagree with the one shown on Review & Create.
//
// A PRIVATE league hands over a CREDENTIAL, not an address: games.invite_code
// ("CARTER25"), which QUSHi generates and the organiser distributes — as a
// typeable code, or as the link that carries it. The slug is only the league's
// URL and grants nothing on its own. A PUBLIC league has no credential to give,
// so it shares its address and shows no code block at all.
const COPY = {
  private: {
    heading: "Private League Created",
    lede: "Drop the code. Your circle is waiting.",
    // Where the shared link actually points. /join/ is what tells the app a
    // league is private; /leagues/ is the public equivalent.
    path: "join",
  },
  public: {
    heading: "Public League Created",
    lede: "Share it. Let the competition come to you.",
    path: "leagues",
  },
};

const LeagueCreatedSuccess = ({ variant: variantKey = "private" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { inviteCode: issuedCode, vanityUrl, title } = location.state || {};
  const [codeCopied, setCodeCopied] = useState(false);
  const containerRef = useRef(null);

  // Client Rev 1, private Screen 5: "No QUSHi logo, no three dot menu, no bottom
  // navigation. Keep one back arrow." The app header is suppressed by route
  // (Contest.jsx) and the bottom navigation too (AuthLayout); this is what gives
  // the layout back the height that was reserved for that navigation, so the
  // page does not end in a dead band.
  // Mobile only — client Rev 3 puts the desktop payoff back in the normal QUSHi
  // shell. Same 768px switch as LeagueWizard and PublicLeagueSubmitted.
  const focused = useWindowWidth() < 768;
  useFocusedFlowChrome(focused);

  // "make sure the screen opens to the top of the page when coming from screen 4"
  useEffect(() => {
    scrollToTop(containerRef.current);
  }, []);

  const copyText = COPY[variantKey] || COPY.private;
  const linkPrefix = (VARIANTS[variantKey] || VARIANTS.private).linkPrefix;

  // Reached without the state a submit hands over (a refresh, or a pasted URL) —
  // there's nothing to show, so send the user back to League Play.
  if (!vanityUrl) {
    navigate("/contest", { replace: true });
    return null;
  }

  // TWO different things, deliberately:
  //   the code — a credential QUSHi issues for a private league (CARTER25). It's
  //              what grants access, and it's what the invite link carries.
  //   the slug — the league's address. Knowing it grants nothing.
  // A public league needs no credential, so it shares its address instead.
  const isPrivate = variantKey === "private";
  const inviteCode = issuedCode || "";
  // What's DISPLAYED is the league's readable address, per the design:
  //   qushi.io/join/carter-investing-club
  // What's actually SHARED carries the credential too, because the address alone
  // grants nothing — that's the point of the code existing:
  //   https://…/join/carter-investing-club?c=CARTER25
  // Opening that validates the invite with nothing to type; strip the query and
  // you're back to the gate.
  const inviteLink = `${linkPrefix}${vanityUrl}`;
  const fullLink =
    isPrivate && inviteCode
      ? `${window.location.origin}/${copyText.path}/${vanityUrl}?c=${encodeURIComponent(inviteCode)}`
      : `${window.location.origin}/${copyText.path}/${vanityUrl}`;

  const copy = async (text, onDone) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      onDone?.();
    } catch (e) {
      toast.error("Could not copy the link. Try again.");
    }
  };

  const handleCopyCode = () => {
    if (!inviteCode) return;
    copy(inviteCode, () => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1500);
    });
  };

  const handleShareLink = async () => {
    // The client's wording, from utils/shareMessage, same as the landing card and
    // the header glyph.
    //
    // THE INVITE CODE LINE IS KEPT, appended after it. The client's spec is about
    // how a shared LANDING PAGE reads and says nothing about invite codes — but
    // this particular button is the one an organiser presses seconds after
    // creating a PRIVATE league, and for those the code is how a recipient gets
    // in. Dropping it to match the spec literally would have removed the only
    // thing that makes the message actionable, which is a change the client did
    // not ask for. Public leagues and Cash Games have no code, so they send
    // exactly the three blocks specified.
    // Client Rev 3 rewrote the private share message, and this screen used to
    // compose its own version of it ("Use invite code X to join." bolted onto the
    // public text). It now asks utils/shareMessage for the payload like every
    // other share control, so the three cannot drift: "all three share places…
    // use the same setup for image and text copy."
    const shareData = leagueShareData(
      { title, game_type: isPrivate ? "private" : "public", invite_code: inviteCode },
      fullLink
    );

    // In the app, Flutter opens the sheet — see utils/shareLink.js. Falls
    // straight through to the existing paths in a browser, or in an app build
    // that does not handle the "share" event yet.
    if (shareViaFlutter(shareData)) return;
    if (navigator.share) {
      try {
        // The whole message as `text`, no `url` — see the note in
        // utils/shareLink.js. With `url` present iOS hands Apple Messages the
        // link alone and drops the body, which is exactly what the client saw:
        // a bare preview card with none of the wording above it.
        await navigator.share({
          title: shareData.title,
          text: shareData.message,
        });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    copy(fullLink, () => toast.success("Link copied to clipboard."));
  };

  return (
    <div
      ref={containerRef}
      className="min-h-[calc(100vh-6rem)] pb-12 bg-white md:bg-transparent"
      // Only the focused mobile shell has a fixed header of its own to clear.
      style={focused ? { paddingTop: HEADER_HEIGHT } : undefined}
    >
      {/* The payoff's header is the back arrow alone — no title, per the design.
          `replace` is what closes the creation flow: "Back returns to League Play
          Home" and "Creation flow is closed and cannot be reopened from Back",
          so the browser's Back button must not land on Step 4 either. */}
      {focused && (
        <WizardHeader
          onBack={() => navigate("/contest", { replace: true })}
          backLabel="Back to League Play"
        />
      )}

      <div className="max-w-screen-sm mx-auto px-4 md:px-0 pt-2">

        {/* On desktop the payoff sits on its own card, as the design has it —
            it's a destination, not another step of a form. */}
        {/* Client Rev 3, private Screen 5b: "Reduce top padding above the
            illustration by approximately 20 to 24 px." 40px -> 16px is 24px off
            the top; the bottom of the card is unchanged. */}
        <div className="md:rounded-2xl md:border md:border-gray-200 md:bg-white md:px-12 md:pt-4 md:pb-10 md:mt-2">
        <div className="flex flex-col items-center text-center pt-2 md:pt-0">
          {/* Client Rev 1, private Screen 5: "make sure the image is this" — the
              private-league mark they supplied, the same one the Create a League
              sheet now shows beside "Private League". One mark for the private
              flow, start to finish.
              (The public branch is dead: /contest/public-league-created renders
              PublicLeagueSubmitted now. Kept so this component still stands on
              its own if anything else ever mounts it as "public".) */}
          {variantKey === "public" ? (
            <GlobeIcon className="w-32 h-32 md:w-40 md:h-40" stroke="#22A36B" />
          ) : (
            // Client Rev 3, private Screen 5a: "Reduce private league
            // illustration to approximately 170 to 180 px." 192px -> 176px
            // (md:w-44). Mobile is untouched.
            <PrivateLeagueMarkIcon className="w-40 h-40 md:w-44 md:h-44" />
          )}
          <h1 className="mt-4 font-montserrat font-bold text-title-subpage md:text-[2.5rem] md:leading-tight text-pageTitle">
            {copyText.heading}
          </h1>
          <p className="mt-2 text-sm md:text-lg text-secondary">{copyText.lede}</p>
        </div>

        {/* Two ways to hand the league over: stacked on mobile where the code is
            the hero, side by side on desktop. The code block only exists where a
            code does — a public league has nothing to keep anyone out. */}
        <div className={`mt-8 grid grid-cols-1 gap-4 md:gap-6 items-stretch ${inviteCode ? "md:grid-cols-2" : ""}`}>
          {inviteCode && (
          <div className="rounded-2xl border border-gray-200 bg-[#F8FAFA] p-5 md:p-6 flex flex-col">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary">
              Invite code
            </p>
            <p className="mt-3 flex-1 font-montserrat font-bold text-2xl md:text-3xl tracking-wide text-[#0F4146] break-all">
              {inviteCode}
            </p>
            {/* Both share actions are outlined — neither is the screen's primary
                move; View League is. */}
            <button
              type="button"
              onClick={handleCopyCode}
              className="mt-4 w-full rounded-xl border border-[#0F4146] bg-white text-[#0F4146] py-3 font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#0F4146] hover:text-white transition-colors"
            >
              <CopyIcon className="w-4 h-4" />
              {codeCopied ? "Copied!" : "Copy code"}
            </button>
          </div>
          )}

          {/* The link carries the CODE for a private league, so opening it is
              already a validated invite — nothing to type. */}
          <div className="rounded-2xl border border-gray-200 bg-[#F8FAFA] p-5 md:p-6 flex flex-col">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary">
              Invite link
            </p>
            {/* One line with an ellipsis: the address is for recognising, not for
                reading — Share link is what actually hands it over. */}
            <p className="mt-3 flex-1 text-base text-[#0F172A] truncate" title={inviteLink}>
              {inviteLink}
            </p>
            <button
              type="button"
              onClick={handleShareLink}
              className="mt-4 w-full rounded-xl border border-[#0F4146] bg-white text-[#0F4146] py-3 font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#0F4146] hover:text-white transition-colors"
            >
              <ShareUpIcon className="w-4 h-4" />
              Share link
            </button>
          </div>
        </div>

        {/* The hairline separates handing the league out from going into it. */}
        <div className="mt-8 border-t border-divider pt-8 flex justify-center">
          {/* Client Rev 3, private Screen 5c: "Set the View League button to
              approximately 480 x 56 px, centered." The flex parent centres it. */}
          <button
            type="button"
            onClick={() => navigate(`/contest/${vanityUrl}`)}
            className="w-full md:w-[480px] md:max-w-full rounded-xl bg-[#0F4146] text-white px-12 py-3.5 md:py-0 md:h-14 font-semibold hover:bg-[#0B3A3F] transition-colors"
          >
            View league
          </button>
        </div>

        {/* Client Rev 3, private Screen 5d: "Keep the closing helper on one line,
            and remove the extra 'your' before 'My Leagues'." It read "...from
            your My Leagues"; `whitespace-nowrap` is what keeps the one line a
            promise rather than a hope.
            The destination is the same fix the client asked for on the public
            payoff (5e): the My Leagues SCREEN on its Starting Soon tab, not
            League Play home. Applied here too — it is the same sentence, the
            same link and the same wrong destination; leaving one of the two
            pointing at the old target would only come back as another ticket. */}
        {/* Client Rev 4, MOBILE: "'My Leagues' is not on line two by itself."
            It was wrapping wherever the box ran out, which split the link across
            two lines ("...from My" / "Leagues."). The sentence now breaks on
            purpose: the lead is its own block below `md`, and the link plus its
            full stop are held together, so line two is exactly "My Leagues.".
            Desktop is unchanged — `md:inline` puts it back on one line and the
            parent still carries `md:whitespace-nowrap` (client Rev 3, 5d: "Keep
            the closing helper on one line"). */}
        <p className="mt-4 text-center text-sm text-secondary md:whitespace-nowrap">
          <span className="block md:inline">
            You can manage your league settings from
          </span>{" "}
          <span className="whitespace-nowrap">
            <Link
              to="/contest/my-games?tab=soon"
              className="font-semibold text-[#0F172A] hover:underline"
            >
              My Leagues
            </Link>
            .
          </span>
        </p>
        </div>
      </div>
    </div>
  );
};

export default LeagueCreatedSuccess;
